import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DsBadgeComponent } from '@design-system/ds-badge/component/ds-badge.component';
import { DsButtonComponent } from '@design-system/ds-button/component/ds-button.component';
import { DsInputComponent } from '@design-system/ds-input/component/ds-input.component';
import { DsTextareaComponent } from '@design-system/ds-textarea/component/ds-textarea.component';
import { SrTimezoneDatePipe } from '@pipes/sr-timezone-date/sr-timezone-date.pipe';
import { extractHttpErrorMessage } from '@utils/extract-http-error-message.helper';
import { finalize, take } from 'rxjs';
import { IbRepoVerificationDialogData } from '../../interfaces/ib-repo-verification-dialog-data.interface';
import { IbRepoVerificationService } from '../../services/ib-repo-verification.service';
import {
  IB_REPO_NOTE_MAX_LENGTH,
  IB_REPO_REASON_TEXTS,
  IB_REPO_VERIFIED_HOURS_VALIDATORS,
} from '../../properties/ib-repo-verification.properties';

const GENERIC_ERROR = 'Failed to save the verification. Please try again.';

@Component({
  selector: 'app-ib-repo-verification-dialog',
  templateUrl: './ib-repo-verification-dialog.component.html',
  styleUrls: ['./ib-repo-verification-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    SrTimezoneDatePipe,
    ReactiveFormsModule,
    MatDialogModule,
    DsBadgeComponent,
    DsButtonComponent,
    DsInputComponent,
    DsTextareaComponent,
  ],
})
export class IbRepoVerificationDialogComponent {
  readonly data: IbRepoVerificationDialogData = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<IbRepoVerificationDialogComponent, boolean>);
  private readonly service = inject(IbRepoVerificationService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.group({
    verifiedHours: this.fb.control<number | string | null>(this.getInitialHours(), [...IB_REPO_VERIFIED_HOURS_VALIDATORS]),
    note: this.fb.control<string | null>(this.data.note ?? null, [Validators.maxLength(IB_REPO_NOTE_MAX_LENGTH)]),
  });

  pending = false;
  errorMessage: string | null = null;

  get reasonText(): string {
    return (this.data.reason && IB_REPO_REASON_TEXTS[this.data.reason]) || '';
  }

  get isVerified(): boolean {
    return this.data.verifiedHours !== null && this.data.verifiedHours !== undefined;
  }

  get isSaveDisabled(): boolean {
    return this.pending || this.form.invalid;
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  save(): void {
    if (this.isSaveDisabled) {
      return;
    }

    const { verifiedHours, note } = this.form.getRawValue();
    const trimmedNote = note?.trim();

    this.pending = true;
    this.errorMessage = null;

    this.service
      .verify(this.data.blockTimeRecordId, {
        verifiedHours: Number(verifiedHours),
        note: trimmedNote ? trimmedNote : null,
      })
      .pipe(
        take(1),
        finalize(() => {
          this.pending = false;
          this.cd.markForCheck();
        }),
      )
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (err: unknown) => {
          this.errorMessage = extractHttpErrorMessage(err, GENERIC_ERROR);
          this.cd.markForCheck();
        },
      });
  }

  private getInitialHours(): number | null {
    return this.data.verifiedHours ?? this.data.capturedHours ?? this.data.expectedHours ?? null;
  }
}
